import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/** Catálogo público: verticales (rubros) y planes por vertical. */

export interface Vertical {
  id: string;
  name: string;
  slug: string;
  icon: string;
}

export interface PlanFeature {
  key: string;
  name: string;
}

export interface Plan {
  id: string;
  vertical_id: string;
  name: string;
  price_monthly: number;
  price_yearly: number;
  currency: string;
  max_projects: number;
  max_users: number;
  /** Cuota de almacenamiento de archivos adjuntos, en MB. */
  max_storage_mb: number;
  features: PlanFeature[];
}

export function useVerticals() {
  return useQuery({
    queryKey: ['verticals'],
    queryFn: () => api.get<Vertical[]>('/verticals'),
    staleTime: 5 * 60_000,
  });
}

export function usePlans(verticalId: string | null) {
  return useQuery({
    queryKey: ['plans', verticalId],
    queryFn: () => api.get<Plan[]>(`/plans?vertical_id=${encodeURIComponent(verticalId as string)}`),
    enabled: Boolean(verticalId),
    staleTime: 5 * 60_000,
  });
}

/**
 * Formatea una cuota de almacenamiento en MB de forma amigable:
 * < 1024 MB → "512 MB"; múltiplos de GB → "2 GB" / "1.5 GB".
 */
export function formatStorageMb(mb: number): string {
  if (!Number.isFinite(mb) || mb <= 0) return '0 MB';
  if (mb < 1024) return `${mb} MB`;
  const gb = mb / 1024;
  const text = Number.isInteger(gb) ? String(gb) : gb.toFixed(1);
  return `${text} GB`;
}

/** Formatea un importe con la moneda del plan (PYG / BRL / USD). */
export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('es', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol', // ₲ / R$ / US$ en vez del código (PYG/BRL/USD)
      maximumFractionDigits: currency === 'PYG' ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}
